import { useCallback, useRef, useState, useEffect } from 'react';

interface UseAutoSaveOptions {
  delay?: number; // Delay in milliseconds before saving
  onSave: (content: string) => void | Promise<void>;
}

interface UseAutoSaveReturn {
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved: Date | null;
  triggerSave: (content: string) => void;
}

/**
 * Custom hook for auto-saving content with debouncing
 * Uses throttle pattern for safety - prevents data loss if something breaks
 */
export function useAutoSave({
  delay = 2000,
  onSave,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const triggerSave = useCallback(
    (content: string) => {
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set status to indicate changes are pending
      if (saveStatus === 'saved') {
        setSaveStatus('idle');
      }

      // Set new timeout for debounced save
      timeoutRef.current = setTimeout(async () => {
        // Prevent concurrent saves
        if (isSavingRef.current) return;

        try {
          isSavingRef.current = true;
          setSaveStatus('saving');

          await onSave(content);

          setSaveStatus('saved');
          setLastSaved(new Date());
        } catch (error) {
          console.error('Auto-save failed:', error);
          setSaveStatus('error');
        } finally {
          isSavingRef.current = false;
        }
      }, delay);
    },
    [delay, onSave, saveStatus]
  );

  return {
    saveStatus,
    lastSaved,
    triggerSave,
  };
}
